import { useEffect, useState } from 'react';
import { browserTimeZone, formatDateTime, formatRelative } from './format.js';

/** A shared clock that ticks every `intervalMs`, so relative times stay current on a long-open page. */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const handle = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(handle);
  }, [intervalMs]);
  return now;
}

export interface RelativeTimeProps {
  /** ISO instant. */
  value: string | null | undefined;
  /**
   * `relative`: "3 days ago" with the absolute in title and accessible name (04-data §4).
   * `absolute`: "16 Sep 2026, 09:00 UTC". `both`: relative, then the absolute in parentheses —
   * use it where a touch reader must reach the instant without hover (04-data §6.9).
   */
  mode?: 'relative' | 'absolute' | 'both';
  timeZone?: string;
  className?: string;
  /** Rendered when the value is null: the em dash by default. */
  fallback?: string;
}

export function RelativeTime({ value, mode = 'relative', timeZone, className, fallback = '—' }: RelativeTimeProps) {
  const now = useNow();
  if (!value) return <span className={className}>{fallback}</span>;
  const zone = timeZone ?? browserTimeZone();
  const absolute = formatDateTime(value, { timeZone: zone });
  if (mode === 'absolute') {
    return <time dateTime={value} className={className}>{absolute}</time>;
  }
  const relative = formatRelative(value, now);
  if (mode === 'both') {
    return (
      <time dateTime={value} className={className}>
        {relative} <span className="muted">({absolute})</span>
      </time>
    );
  }
  return (
    <time dateTime={value} title={absolute} className={className}>
      <span aria-hidden="true">{relative}</span>
      <span className="visually-hidden">{`${relative}, ${absolute}`}</span>
    </time>
  );
}
